import clsx from 'clsx'

import type { ComponentProps, JSX } from 'react'

/**
 * The card treatment behind legacy `.mock` (product mock-ups) and `.surface` (full-width section
 * washes) — one component, because measuring both on live production (`https://www.sonicjobs.com/`,
 * 2026-09-23) turned up the same background family and only `.mock` adds the edge, so the richer
 * (card) treatment is what this primitive offers.
 *
 * Measured via `getComputedStyle` on `.mock` in both themes:
 *   - `background-color: rgb(247,247,245)` light / `rgb(13,13,13)` dark — exactly `--color-surface`.
 *   - `border: 1px solid rgba(13,13,13,.16)` light / `rgba(255,255,255,.16)` dark, all four sides —
 *     exactly `--color-border-strong`. The legacy source's `var(--sj-color-divider)` is NOT what
 *     production renders; production's border is twice as strong as the plain `--color-border` hairline.
 *   - `border-radius: 16px` — `--radius-lg`, not the legacy source's `--sj-radius-surface` (10px), which
 *     no measured production radius uses at all (7 distinct radii, none is 10px: rows 49-55 of
 *     docs/superpowers/specs/2026-09-22-s20-825-marketing-design-system-deltas.md).
 *   - `box-shadow: none` on every `.mock` element on both routes. The legacy source declares
 *     `var(--sj-shadow-surface)`; production does not render it (confirmed: the deltas doc's
 *     exhaustive box-shadow rows 82-84 plus the drawer note, and `.mock` is not among them).
 *     No shadow utility is applied here — adding one would be a value with no production backing.
 *   - `overflow: hidden`, needed because mock-ups place images/screenshots flush against the radius.
 *
 * Only the treatment is offered. Width, margin and position are the parent's job, forwarded via
 * `className`.
 */
export default function Surface({ className, ...props }: ComponentProps<'div'>): JSX.Element {
  return (
    <div {...props} className={clsx('bg-surface border border-border-strong rounded-lg overflow-hidden', className)} />
  )
}
